import { productService } from "@/services/api/product.service";
import { productModel } from "@/models/api/product.model";
import { sharedHelper, alertHelper } from "@/utils";
import i18n from "@/i18n";

export const PRODUCT_MODULE = "api/product";
export const GET_ALL_PRODUCT = "getAllProduct";
export const CREATE_PRODUCT = "createProduct";
export const GET_PRODUCT = "getProduct";
export const UPDATE_PRODUCT = "updateProduct";
export const UPDATE_PRODUCT_STATUS = "updateProductStatus";
export const DELETE_PRODUCT = "deleteProduct";

export const state = {
  list: {
    data: [],
    pagination: {
      pageNumber: 1,
      pageSize: 15,
      lastPage: 1,
      tableRowsCounts: 0,
      total: 0,
    },
  },
  details: {},
  listError: false,
  status: {
    list: true,
    detail: true,
    save: true,
  },
};

function showError(commit, error, type = "save") {
  commit("failure", type);
  alertHelper.alertError({
    message: sharedHelper.getErrorMessage(error),
    title: i18n.t("message.error"),
  });
}

function requireSuccess(result) {
  if (!result?.success)
    throw new Error(result?.message || "Product request failed.");
  return result;
}

async function refresh(dispatch, filter) {
  await dispatch(GET_ALL_PRODUCT, { filter });
}

export const actions = {
  async [GET_ALL_PRODUCT]({ commit }, { filter }) {
    commit("requesting");
    try {
      const result = await productService.getAll(filter);
      requireSuccess(result);
      commit("listSuccess", result);
      return result;
    } catch (error) {
      showError(commit, error, "list");
      return null;
    }
  },
  async [CREATE_PRODUCT]({ commit, dispatch }, { data, filter }) {
    commit("requesting", "save");
    try {
      const result = await productService.store(data);
      requireSuccess(result);
      commit("success");
      alertHelper.alertSuccess({
        message: i18n.t("message.createXSuccess", [i18n.t("module.product")]),
      });
      await refresh(dispatch, filter);
      return result;
    } catch (error) {
      showError(commit, error);
      return null;
    }
  },
  async [GET_PRODUCT]({ commit }, { uuid }) {
    commit("requesting", "detail");
    try {
      const result = await productService.show(uuid);
      requireSuccess(result);
      commit("detailSuccess", result);
      return result;
    } catch (error) {
      showError(commit, error);
      return null;
    }
  },
  async [UPDATE_PRODUCT]({ commit, dispatch }, { data, filter }) {
    commit("requesting", "save");
    try {
      const result = await productService.update(data);
      requireSuccess(result);
      commit("success");
      alertHelper.alertSuccess({
        message: i18n.t("message.updateXSuccess", [i18n.t("module.product")]),
      });
      await refresh(dispatch, filter);
      return result;
    } catch (error) {
      showError(commit, error);
      return null;
    }
  },
  async [UPDATE_PRODUCT_STATUS](
    { commit, dispatch },
    { uuid, isActive, filter },
  ) {
    commit("requesting", "save");
    try {
      const result = await productService.updateStatus(uuid, isActive);
      requireSuccess(result);
      commit("success");
      await refresh(dispatch, filter);
      return result;
    } catch (error) {
      showError(commit, error);
      return null;
    }
  },
  async [DELETE_PRODUCT]({ commit, dispatch }, { uuid, filter }) {
    commit("requesting", "save");
    try {
      const result = await productService.softDelete(uuid);
      requireSuccess(result);
      commit("success");
      alertHelper.alertSuccess({
        message: i18n.t("message.deleteXSuccess", [i18n.t("module.product")]),
      });
      await refresh(dispatch, filter);
      return result;
    } catch (error) {
      showError(commit, error);
      return null;
    }
  },
};

export const mutations = {
  requesting(state, type = "list") {
    state.status[type] = false;
    if (type === "list") state.listError = false;
  },
  listSuccess(state, result) {
    state.list.data = (result?.data || []).map(productModel);
    state.list.pagination = sharedHelper.apiPaginationAdapter(
      result?.pagination || {},
    );
    state.status.list = true;
  },
  detailSuccess(state, result) {
    const payload = result?.data?.product || result?.data || {};
    state.details = productModel(payload);
    state.status.detail = true;
  },
  success(state) {
    state.status = {
      list: true,
      detail: true,
      save: true,
    };
  },
  failure(state, type) {
    if (type === "list") state.listError = true;
    state.status = {
      list: true,
      detail: true,
      save: true,
    };
  },
};
